import React, { SyntheticEvent } from 'react'
import { isEmpty } from 'lodash'
import { parseISO } from 'date-fns'
import format from 'libs/dateFns/format'

import { BulletLabel, Skeleton, Thumbnail } from 'components'
import { TBody, Row, ActionEventTable } from 'components/Table'
import { getImageFileName } from 'helpers/common'
import { LIMIT_PAGE } from 'helpers/constants'
import { HEADERS_LOADING } from 'features/Product'

import type { IColumn, ProductForList } from 'types'

interface Props {
  columns: IColumn[]
  products: ProductForList[]
  loading: boolean
  isError: boolean
  branchId: string
  currentPage: number
  onClickRow: (
    branchId: string,
    productId: string,
  ) => (e: SyntheticEvent) => void
}

const SkeletonListOdd: Record<string, React.ReactNode> = {
  no: <Skeleton.whiteSkeleton />,
  thumbnailImage: <Skeleton.whiteSkeleton width='80px' height='80px' />,
  name: <Skeleton.whiteSkeleton width='100%' />,
  serialNumber: <Skeleton.whiteSkeleton />,
  dateOfPurchase: <Skeleton.whiteSkeleton width='100%' />,
}

const SkeletonListEven: Record<string, React.ReactNode> = {
  no: <Skeleton.whiteSkeleton />,
  thumbnailImage: <Skeleton.whiteSkeleton width='80px' height='80px' />,
  name: <Skeleton.whiteSkeleton width='80%' />,
  serialNumber: <Skeleton.whiteSkeleton />,
  dateOfPurchase: <Skeleton.whiteSkeleton width='80%' />,
}

export const ProductList: React.FC<Props> = (props: Props) => {
  const base = 'con-product'
  const {
    currentPage,
    branchId,
    columns,
    products,
    loading,
    isError,
    onClickRow,
  } = props

  const list: Record<string, React.ReactNode>[] = products.map(
    (item, index) => {
      const thumbnailPath = item.photos
        ? getImageFileName(item.photos[0])
        : undefined
      const numberToStart = LIMIT_PAGE * (currentPage - 1) + 1

      return {
        no: (numberToStart + index).toString().padStart(2, '0'),
        thumbnailImage: (
          <Thumbnail className={`${base}__thumbnail`} src={thumbnailPath} />
        ),
        name: item.name,
        serialNumber: item.serialNumber || '-',
        dateOfPurchase: format(parseISO(item.dateOfPurchase), 'dd MMM yyyy'),
        endWarranty: item.endWarranty
          ? format(parseISO(item.endWarranty), 'dd MMM yyyy')
          : '-',
        isActive: <BulletLabel isActive={Boolean(item.isActive)} />,
      }
    },
  )

  if (loading) {
    return (
      <Skeleton.SkeletonList
        columns={HEADERS_LOADING}
        rowOdd={SkeletonListOdd}
        rowEven={SkeletonListEven}
      />
    )
  }

  if (isEmpty(products) || isError) {
    return <ActionEventTable colSpan={columns.length} isNotFound />
  }

  return (
    <TBody>
      {list.map((data, index) => (
        <Row
          data-testid={`product-row-${products[index].id}`}
          key={index}
          columns={columns}
          data={data}
          onClick={onClickRow(branchId, `${products[index].id}`)}
          position='left'
        />
      ))}
    </TBody>
  )
}

export default ProductList
